import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { 
  Plus, 
  Check,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  ShoppingCart,
  Store,
  Sparkles,
  Loader2,
  X,
  Clock,
  CheckCircle2,
  ArrowRight,
  Crown,
  Zap,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import { haptic } from '@/utils/haptics';

// Store options with branding
const STORES = [
  // Popular Delivery Services
  { id: 'instacart', name: 'Instacart', emoji: '🛒', color: 'bg-green-500', textColor: 'text-white', category: 'delivery' },
  
  // National Chains
  { id: 'walmart', name: 'Walmart', emoji: '🏪', color: 'bg-blue-600', textColor: 'text-white', category: 'national' },
  { id: 'amazon', name: 'Amazon Fresh', emoji: '📦', color: 'bg-orange-500', textColor: 'text-white', category: 'national' },
  { id: 'target', name: 'Target', emoji: '🎯', color: 'bg-red-600', textColor: 'text-white', category: 'national' },
  { id: 'costco', name: 'Costco', emoji: '🛒', color: 'bg-red-700', textColor: 'text-white', category: 'national' },
  { id: 'sams', name: "Sam's Club", emoji: '🏪', color: 'bg-blue-700', textColor: 'text-white', category: 'national' },
  { id: 'wholefoods', name: 'Whole Foods', emoji: '🥗', color: 'bg-green-700', textColor: 'text-white', category: 'national' },
  { id: 'kroger', name: 'Kroger', emoji: '🏬', color: 'bg-red-600', textColor: 'text-white', category: 'national' },
  { id: 'cvs', name: 'CVS', emoji: '💊', color: 'bg-red-500', textColor: 'text-white', category: 'national' },
  { id: 'walgreens', name: 'Walgreens', emoji: '💊', color: 'bg-red-600', textColor: 'text-white', category: 'national' },
  
  // Regional Favorites
  { id: 'publix', name: 'Publix', emoji: '🦅', color: 'bg-green-600', textColor: 'text-white', category: 'regional', region: 'Southeast' },
  { id: 'heb', name: 'H-E-B', emoji: '🐻', color: 'bg-red-600', textColor: 'text-white', category: 'regional', region: 'Texas' },
  { id: 'safeway', name: 'Safeway', emoji: '🌲', color: 'bg-red-500', textColor: 'text-white', category: 'regional', region: 'West' },
  { id: 'albertsons', name: 'Albertsons', emoji: '🌲', color: 'bg-blue-600', textColor: 'text-white', category: 'regional', region: 'West' },
  { id: 'aldi', name: 'ALDI', emoji: '🍎', color: 'bg-blue-500', textColor: 'text-white', category: 'regional' },
  { id: 'traderjoes', name: "Trader Joe's", emoji: '🛍️', color: 'bg-red-700', textColor: 'text-white', category: 'regional' },
];

// Aisle categories for grouping
const AISLE_ORDER = [
  { key: 'Produce', emoji: '🥬', color: 'bg-green-100 dark:bg-green-900/30' },
  { key: 'Meat', emoji: '🥩', color: 'bg-red-100 dark:bg-red-900/30' },
  { key: 'Seafood', emoji: '🐟', color: 'bg-blue-100 dark:bg-blue-900/30' },
  { key: 'Dairy', emoji: '🥛', color: 'bg-yellow-100 dark:bg-yellow-900/30' },
  { key: 'Bakery', emoji: '🍞', color: 'bg-amber-100 dark:bg-amber-900/30' },
  { key: 'Pantry', emoji: '🧂', color: 'bg-orange-100 dark:bg-orange-900/30' },
  { key: 'Frozen', emoji: '🧊', color: 'bg-cyan-100 dark:bg-cyan-900/30' },
  { key: 'Beverages', emoji: '🥤', color: 'bg-purple-100 dark:bg-purple-900/30' },
  { key: 'Household', emoji: '🧴', color: 'bg-slate-100 dark:bg-slate-900/30' },
  { key: 'Other', emoji: '📦', color: 'bg-gray-100 dark:bg-gray-900/30' },
];

// Common quick-add items
const QUICK_ADD_ITEMS = [
  { name: 'Milk', aisle: 'Dairy' },
  { name: 'Eggs', aisle: 'Dairy' },
  { name: 'Bread', aisle: 'Bakery' },
  { name: 'Bananas', aisle: 'Produce' },
  { name: 'Butter', aisle: 'Dairy' },
  { name: 'Chicken', aisle: 'Meat' },
];

// Smart suggestions
const FORGOT_SOMETHING = [
  { name: 'Salt', aisle: 'Pantry' },
  { name: 'Olive Oil', aisle: 'Pantry' },
  { name: 'Butter', aisle: 'Dairy' },
  { name: 'Garlic', aisle: 'Produce' },
];

/**
 * Mobile-optimized Grocery List Component
 * With streamlined "Tap → Store → Shop" export flow
 */
const MobileGroceryList = ({ 
  list, 
  onUpdateItem, 
  onAddItem, 
  onExport,
  isPro,
  allLists,
  onSwitchList,
  listId
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  
  // Sheet states
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const [exportSheetOpen, setExportSheetOpen] = useState(false);
  
  // Export flow states
  const [exportStep, setExportStep] = useState('select'); // 'select', 'confirm', 'upgrade', 'loading'
  const [selectedStore, setSelectedStore] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [lastUsedStore, setLastUsedStore] = useState(() => 
    localStorage.getItem('lastExportStore') || 'instacart'
  );
  
  // In-store mode
  const [inStoreMode, setInStoreMode] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showForgotPrompt, setShowForgotPrompt] = useState(false);
  const [newItemName, setNewItemName] = useState('');

  // Check for post-upgrade redirect
  const justUpgraded = searchParams.get('upgraded') === 'true';
  const autoExportStore = searchParams.get('autoExport');
  
  useEffect(() => {
    if (justUpgraded && autoExportStore && isPro) {
      // Magic moment: Auto-open export after upgrade
      toast.success('🎉 Pro unlocked!', { duration: 2000 });
      haptic('success');
      
      // Auto-trigger export
      setTimeout(() => {
        setSelectedStore(STORES.find(s => s.id === autoExportStore) || STORES[0]);
        setExportStep('confirm');
        setExportSheetOpen(true);
      }, 500);
    }
  }, [justUpgraded, autoExportStore, isPro]);

  // Group items by aisle
  const groupedItems = {};
  const checkedItems = [];
  
  (list?.items || []).forEach((item, idx) => {
    const itemWithIndex = { ...item, originalIndex: idx };
    if (item.checked) {
      checkedItems.push(itemWithIndex);
    } else {
      const aisle = item.aisle || 'Other';
      if (!groupedItems[aisle]) {
        groupedItems[aisle] = [];
      }
      groupedItems[aisle].push(itemWithIndex);
    }
  });

  const sortedAisles = AISLE_ORDER.filter(a => groupedItems[a.key]);
  
  // Stats
  const totalItems = list?.items?.length || 0;
  const checkedCount = checkedItems.length;
  const uncheckedCount = totalItems - checkedCount;
  const progress = totalItems > 0 ? (checkedCount / totalItems) * 100 : 0;

  // Show "forgot something" when nearly done
  useEffect(() => {
    if (progress >= 80 && progress < 100 && !showForgotPrompt) {
      setShowForgotPrompt(true);
    }
  }, [progress, showForgotPrompt]);

  // Keep screen awake in store mode
  useEffect(() => {
    if (inStoreMode && 'wakeLock' in navigator) {
      let wakeLock = null;
      const requestWakeLock = async () => {
        try {
          wakeLock = await navigator.wakeLock.request('screen');
        } catch (err) {
          console.log('Wake Lock error:', err);
        }
      };
      requestWakeLock();
      return () => { if (wakeLock) wakeLock.release(); };
    }
  }, [inStoreMode]);

  // Handle item toggle
  const handleToggleItem = (itemIndex, currentChecked) => {
    // Use different haptic patterns based on context
    if (!currentChecked) {
      // Checking off an item - satisfying tick!
      haptic(inStoreMode ? 'tick' : 'success');
      toast.success(inStoreMode ? '✓' : 'Got it!', { 
        duration: inStoreMode ? 500 : 1000,
        position: inStoreMode ? 'bottom-center' : 'top-center'
      });
    } else {
      // Unchecking an item
      haptic('light');
    }
    onUpdateItem(itemIndex, { checked: !currentChecked });
  };

  // Handle add item with smart merge
  const handleAddItem = (name, aisle = 'Other') => {
    if (!name.trim()) return;
    
    const existingIndex = list?.items?.findIndex(item => 
      item.name.toLowerCase().includes(name.toLowerCase()) ||
      name.toLowerCase().includes(item.name.toLowerCase())
    );
    
    if (existingIndex >= 0) {
      const existing = list.items[existingIndex];
      const newAmount = (existing.amount || 1) + 1;
      onUpdateItem(existingIndex, { amount: newAmount });
      haptic('success');
      toast.success(`Merged with ${existing.name}`, { duration: 2000 });
    } else {
      onAddItem({ name: name.trim(), aisle, amount: 1, checked: false });
      haptic('success');
      toast.success('Added!', { duration: 1000 });
    }
    
    setNewItemName('');
    setAddSheetOpen(false);
  };

  // ==================== EXPORT FLOW ====================
  
  // Step 1: Open export sheet
  const handleShopTap = () => {
    haptic('medium');
    setExportStep('select');
    setSelectedStore(null);
    setExportSheetOpen(true);
  };

  // Step 2: Select store
  const handleStoreSelect = (store) => {
    haptic('light');
    setSelectedStore(store);
    setExportStep('confirm');
  };

  // Step 3: Confirm and export (or show upgrade)
  const handleConfirmExport = async () => {
    if (!isPro) {
      // Show upgrade gate
      haptic('medium');
      setExportStep('upgrade');
      return;
    }
    
    // Pro user - proceed with export
    await executeExport();
  };

  // Execute the actual export
  const executeExport = async () => {
    haptic('medium');
    setExportStep('loading');
    setExportLoading(true);
    
    try {
      // Save last used store
      localStorage.setItem('lastExportStore', selectedStore.id);
      setLastUsedStore(selectedStore.id);
      
      await onExport(selectedStore.id);
      
      toast.success(`🎉 Sending to ${selectedStore.name}...`, { duration: 2000 });
      setExportSheetOpen(false);
      
      // Reset state
      setTimeout(() => {
        setExportStep('select');
        setSelectedStore(null);
      }, 300);
    } catch (error) {
      toast.error('Export failed. Please try again.');
      setExportStep('confirm');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle upgrade tap
  const handleUpgradeTap = () => {
    haptic('medium');
    // Navigate to subscription with return URL
    const returnUrl = `/grocery-lists/${listId}?upgraded=true&autoExport=${selectedStore.id}`;
    navigate(`/subscription?returnUrl=${encodeURIComponent(returnUrl)}`);
  };

  // Handle "Continue without exporting"
  const handleSkipUpgrade = () => {
    haptic('light');
    setExportSheetOpen(false);
    toast('No problem! You can always upgrade later.', { duration: 3000 });
  };

  // Estimated total
  const estimatedTotal = (list?.items || []).reduce((acc, item) => {
    return acc + (item.amount || 1) * 3.50;
  }, 0);

  if (!list) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen pb-32 ${inStoreMode ? 'bg-black text-white' : ''}`} data-testid="mobile-grocery-list">
      {/* Header */}
      <div className={`sticky top-0 z-10 px-4 py-3 border-b ${inStoreMode ? 'bg-black border-gray-800' : 'bg-background/95 backdrop-blur-sm'}`}>
        <div className="flex items-center justify-between mb-2">
          <button 
            onClick={() => navigate('/grocery-lists')}
            className="flex items-center gap-1 text-sm text-muted-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            Lists
          </button>
          
          <div className="flex items-center gap-2">
            <Store className={`h-4 w-4 ${inStoreMode ? 'text-green-400' : 'text-muted-foreground'}`} />
            <Switch 
              checked={inStoreMode}
              onCheckedChange={(checked) => {
                haptic('medium');
                setInStoreMode(checked);
                if (checked) {
                  toast.success('In-store mode on', { description: 'Screen will stay awake', duration: 2000 });
                }
              }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h1 className={`font-semibold ${inStoreMode ? 'text-xl' : 'text-lg'} font-['Playfair_Display']`}>
              {list.name || 'Grocery List'}
            </h1>
            <p className={`text-sm ${inStoreMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
              {uncheckedCount} items left
            </p>
          </div>
          
          <div className="text-right">
            <p className={`text-xs ${inStoreMode ? 'text-gray-500' : 'text-muted-foreground'}`}>Estimated</p>
            <p className={`font-semibold ${inStoreMode ? 'text-lg' : ''}`}>~${estimatedTotal.toFixed(0)}</p>
          </div>
        </div>

        <div className="mt-3 h-1.5 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-green-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Export history hint */}
        {lastUsedStore && !inStoreMode && (
          <button 
            onClick={() => {
              const store = STORES.find(s => s.id === lastUsedStore);
              if (store) {
                setSelectedStore(store);
                setExportStep('confirm');
                setExportSheetOpen(true);
              }
            }}
            className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"
          >
            <Clock className="h-3 w-3" />
            Last sent to {STORES.find(s => s.id === lastUsedStore)?.name}
          </button>
        )}
      </div>

      {/* Items List */}
      <div className="px-4 py-4 space-y-4">
        {sortedAisles.map(({ key, emoji, color }) => (
          <div key={key}>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-2 ${inStoreMode ? 'bg-gray-900' : color}`}>
              <span className="text-lg">{emoji}</span>
              <span className={`font-medium ${inStoreMode ? 'text-white' : ''}`}>{key}</span>
              <Badge variant="secondary" className="ml-auto text-xs">{groupedItems[key].length}</Badge>
            </div>

            <div className="space-y-1">
              {groupedItems[key].map((item) => (
                <GroceryItemRow
                  key={item.originalIndex}
                  item={item}
                  inStoreMode={inStoreMode}
                  onToggle={() => handleToggleItem(item.originalIndex, item.checked)}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Completed Items */}
        {checkedCount > 0 && (
          <Collapsible open={showCompleted} onOpenChange={setShowCompleted}>
            <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500" />
                <span className="font-medium text-sm">Completed</span>
                <Badge variant="secondary" className="text-xs">{checkedCount}</Badge>
              </div>
              {showCompleted ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-1">
              {checkedItems.map((item) => (
                <GroceryItemRow
                  key={item.originalIndex}
                  item={item}
                  inStoreMode={inStoreMode}
                  onToggle={() => handleToggleItem(item.originalIndex, item.checked)}
                  isCompleted
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Forgot Something */}
        {showForgotPrompt && uncheckedCount <= 3 && uncheckedCount > 0 && (
          <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-accent/5">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm">Forgot something?</span>
                <button onClick={() => setShowForgotPrompt(false)} className="ml-auto">
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {FORGOT_SOMETHING.map((item) => (
                  <Button
                    key={item.name}
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs active:scale-95"
                    onClick={() => handleAddItem(item.name, item.aisle)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {item.name}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ==================== STICKY BOTTOM BAR ==================== */}
      {!exportSheetOpen && !addSheetOpen && (
        <div className={`fixed bottom-0 left-0 right-0 p-4 pb-20 border-t z-[10000] ${inStoreMode ? 'bg-black border-gray-800' : 'bg-background/95 backdrop-blur-sm'}`}>
          <div className="flex gap-2 max-w-lg mx-auto">
            {/* Add Item Button */}
            <Button
              variant="outline"
              className={`flex-1 h-14 active:scale-[0.98] ${inStoreMode ? 'bg-gray-900 border-gray-700' : ''}`}
              onClick={() => { haptic('light'); setAddSheetOpen(true); }}
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Item
            </Button>
            
            {/* Shop This List Button - Only show when items exist and not in store mode */}
            {totalItems > 0 && !inStoreMode && (
              <Button
                className="flex-1 h-14 active:scale-[0.98] bg-green-600 hover:bg-green-700 text-white"
                onClick={handleShopTap}
                data-testid="shop-this-list-btn"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Shop this list
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ==================== ADD ITEM SHEET ==================== */}
      <Sheet open={addSheetOpen} onOpenChange={setAddSheetOpen}>
        <SheetContent side="bottom" className="h-[70vh] rounded-t-2xl">
          <SheetHeader className="text-left pb-4">
            <SheetTitle>Add Item</SheetTitle>
          </SheetHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="What do you need?"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddItem(newItemName)}
                className="flex-1 h-12 text-base"
                autoFocus
              />
              <Button className="h-12 px-6" onClick={() => handleAddItem(newItemName)} disabled={!newItemName.trim()}>
                Add
              </Button>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-2">Common items</p>
              <div className="flex flex-wrap gap-2">
                {QUICK_ADD_ITEMS.map((item) => (
                  <Button
                    key={item.name}
                    variant="outline"
                    size="sm"
                    className="h-10 active:scale-95"
                    onClick={() => handleAddItem(item.name, item.aisle)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {item.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ==================== EXPORT FLOW SHEET ==================== */}
      <Sheet open={exportSheetOpen} onOpenChange={(open) => {
        setExportSheetOpen(open);
        if (!open) {
          setTimeout(() => { setExportStep('select'); setSelectedStore(null); }, 300);
        }
      }}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-8 max-h-[85vh]">
          {/* Step 1: Select Store */}
          {exportStep === 'select' && (
            <>
              <SheetHeader className="text-left pb-4">
                <SheetTitle className="text-xl font-['Playfair_Display']">Shop your list</SheetTitle>
                <SheetDescription>Choose where to shop</SheetDescription>
              </SheetHeader>

              <div className="overflow-y-auto max-h-[60vh] space-y-4 pr-2">
                {/* Last Used Store - Show at top if exists */}
                {lastUsedStore && (
                  <div className="mb-2">
                    {STORES.filter(s => s.id === lastUsedStore).map((store) => (
                      <Button
                        key={store.id}
                        variant="outline"
                        className="w-full h-14 justify-start text-base active:scale-[0.98] transition-all border-primary ring-1 ring-primary"
                        onClick={() => handleStoreSelect(store)}
                      >
                        <span className="text-xl mr-3">{store.emoji}</span>
                        <span className="font-medium">{store.name}</span>
                        <Badge variant="secondary" className="ml-auto text-xs">Last used</Badge>
                      </Button>
                    ))}
                  </div>
                )}
                
                {/* Delivery Services */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Delivery Services</p>
                  <div className="space-y-2">
                    {STORES.filter(s => s.category === 'delivery' && s.id !== lastUsedStore).map((store) => (
                      <StoreButton key={store.id} store={store} onSelect={handleStoreSelect} />
                    ))}
                  </div>
                </div>

                {/* National Chains */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">National Chains</p>
                  <div className="space-y-2">
                    {STORES.filter(s => s.category === 'national' && s.id !== lastUsedStore).map((store) => (
                      <StoreButton key={store.id} store={store} onSelect={handleStoreSelect} />
                    ))}
                  </div>
                </div>

                {/* Regional Favorites */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Regional Favorites</p>
                  <div className="space-y-2">
                    {STORES.filter(s => s.category === 'regional' && s.id !== lastUsedStore).map((store) => (
                      <StoreButton key={store.id} store={store} onSelect={handleStoreSelect} />
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Step 2: Confirm */}
          {exportStep === 'confirm' && selectedStore && (
            <>
              <SheetHeader className="text-left pb-4">
                <SheetTitle className="text-xl font-['Playfair_Display']">
                  Send to {selectedStore.name}
                </SheetTitle>
              </SheetHeader>

              <div className="space-y-4">
                {/* Trust indicators */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span>Items matched automatically</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span>Quantities included</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span>You can review before checkout</span>
                  </div>
                </div>

                {/* Item count */}
                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Sending</span>
                    <span className="font-medium">{totalItems} items</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-muted-foreground">Estimated total</span>
                    <span className="font-medium">~${estimatedTotal.toFixed(0)}</span>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 h-12"
                    onClick={() => setExportStep('select')}
                  >
                    Back
                  </Button>
                  <Button
                    className={`flex-1 h-12 ${selectedStore.color} ${selectedStore.textColor} hover:opacity-90 active:scale-[0.98]`}
                    onClick={handleConfirmExport}
                  >
                    Continue to {selectedStore.name}
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Step 3: Upgrade Gate (Free Users Only) */}
          {exportStep === 'upgrade' && selectedStore && (
            <>
              <SheetHeader className="text-center pb-4">
                <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Crown className="h-8 w-8 text-primary" />
                </div>
                <SheetTitle className="text-2xl font-['Playfair_Display']">
                  Finish shopping in one tap
                </SheetTitle>
              </SheetHeader>

              <div className="space-y-4">
                {/* Benefits */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Zap className="h-5 w-5 text-amber-500" />
                    <span>Send lists directly to stores</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Zap className="h-5 w-5 text-amber-500" />
                    <span>Unlimited lists & exports</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Zap className="h-5 w-5 text-amber-500" />
                    <span>No retyping, ever</span>
                  </div>
                </div>

                {/* Upgrade button */}
                <Button
                  className="w-full h-14 text-lg bg-primary hover:bg-primary/90 active:scale-[0.98]"
                  onClick={handleUpgradeTap}
                >
                  <Crown className="h-5 w-5 mr-2" />
                  Upgrade to Pro — $7/month
                </Button>

                {/* Skip option - visible to avoid dark patterns */}
                <Button
                  variant="ghost"
                  className="w-full h-12 text-muted-foreground"
                  onClick={handleSkipUpgrade}
                >
                  Continue without exporting
                </Button>
              </div>
            </>
          )}

          {/* Step 4: Loading */}
          {exportStep === 'loading' && (
            <div className="py-12 text-center">
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
              <p className="text-lg font-medium">Sending to {selectedStore?.name}...</p>
              <p className="text-sm text-muted-foreground mt-1">This will only take a moment</p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

// Item Row Component with enhanced In-Store Mode feedback
const GroceryItemRow = ({ item, inStoreMode, onToggle, isCompleted }) => {
  return (
    <button
      onClick={onToggle}
      className={`w-full flex items-center gap-3 p-4 rounded-lg transition-all active:scale-[0.98] ${
        isCompleted 
          ? 'opacity-50' 
          : item.checked 
            ? inStoreMode 
              ? 'bg-green-900/30 border border-green-800' 
              : 'bg-green-50 dark:bg-green-900/20'
            : inStoreMode 
              ? 'bg-gray-900 hover:bg-gray-800' 
              : 'bg-muted/30 hover:bg-muted/50'
      }`}
      style={{ minHeight: inStoreMode ? '64px' : '56px' }}
      data-testid={`grocery-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Checkbox with animation */}
      <div className={`flex-shrink-0 h-7 w-7 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
        item.checked 
          ? 'bg-green-500 border-green-500 scale-110' 
          : inStoreMode 
            ? 'border-gray-500 hover:border-gray-400' 
            : 'border-muted-foreground/30 hover:border-muted-foreground/50'
      }`}>
        {item.checked && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
      </div>

      {/* Item name */}
      <div className="flex-1 text-left">
        <span className={`font-medium transition-all ${
          inStoreMode ? 'text-xl' : 'text-base'
        } ${
          item.checked ? 'line-through text-muted-foreground' : ''
        }`}>
          {item.name}
        </span>
      </div>

      {/* Amount/unit */}
      {(item.amount > 1 || item.unit) && (
        <span className={`text-sm font-medium ${
          inStoreMode ? 'text-gray-400 text-base' : 'text-muted-foreground'
        }`}>
          {item.amount > 1 ? item.amount : ''} {item.unit || ''}
        </span>
      )}
    </button>
  );
};

// Store Button Component for the export sheet
const StoreButton = ({ store, onSelect }) => {
  return (
    <Button
      variant="outline"
      className="w-full h-12 justify-start text-sm active:scale-[0.98] transition-all"
      onClick={() => onSelect(store)}
    >
      <span className="text-lg mr-3">{store.emoji}</span>
      <span className="font-medium">{store.name}</span>
      {store.region && (
        <span className="ml-auto text-xs text-muted-foreground">{store.region}</span>
      )}
      <ArrowRight className="h-4 w-4 ml-2 text-muted-foreground" />
    </Button>
  );
};

export default MobileGroceryList;
